import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Typography, Button } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import { ToastProvider } from './components/Toast';
import { TOKENS } from './state/colors';
import { EstadoBolsa } from './state/bolsaMachine';
import { BolsaCentro, BolsaConReprecio, MetricasCentro } from './types';
import { centroApi } from './api/centroApi';
import { CotizacionesTab } from './components/CotizacionesTab';
import { PipelineTab } from './components/PipelineTab';
import { ResumenTab } from './components/ResumenTab';
import { QuoteDrawer } from './components/QuoteDrawer';
import { DateRangeControl, calcularRangoPorDefecto } from './components/DateRangeControl';
import { fs } from './state/typography';
import { filtrarPorCliente } from './state/filtroPipeline';
import { NuevaBolsaModal } from '../../components/NuevaBolsaModal';
import { BolsasService } from '../../services/bolsasService';
import { resolverDestinoCrear } from '../../services/resolverDestinoCrear';
import { CreateBolsaDTO } from '../../types';

// SPEC-100 — formato YYYY-MM-DD en horario local (evita el corrimiento de día que
// produciría toISOString() al cruzar la medianoche UTC).
function formatearFechaApi(fecha: Date): string {
  const anio = fecha.getFullYear();
  const mes = String(fecha.getMonth() + 1).padStart(2, '0');
  const dia = String(fecha.getDate()).padStart(2, '0');
  return `${anio}-${mes}-${dia}`;
}

type TabId = 'cotizaciones' | 'pipeline' | 'resumen';

const TABS: { id: TabId; label: string }[] = [
  { id: 'cotizaciones', label: 'Cotizaciones' },
  { id: 'pipeline', label: 'Pipeline' },
  { id: 'resumen', label: 'Resumen' },
];

// CA-008 (SPEC-103) — clave de sessionStorage para conservar tab/filtro/drawer al volver
// de la edición completa (ver comentario junto al useEffect que la consume).
const VOLVER_A_KEY = 'cvcot:centro:volverA';

const CentroCotizacionesContent: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<TabId>('cotizaciones');
  // SPEC-128 (RN-002): modal de "Nueva bolsa" auto-abierto cuando "Crear" del sidebar
  // aterriza aquí sin bolsas ACTIVA/BORRADOR (fallback de resolverDestinoCrear).
  const [nuevaBolsaModalOpen, setNuevaBolsaModalOpen] = useState(false);
  // SPEC-138 (RN-006, VAL-004): mientras resolverDestinoCrear() consulta las bolsas, el
  // botón "Nueva bolsa" queda deshabilitado para evitar navegaciones duplicadas por doble clic.
  const [resolviendoDestino, setResolviendoDestino] = useState(false);
  const [bolsasAbiertas, setBolsasAbiertas] = useState<BolsaCentro[]>([]);
  const [centro, setCentro] = useState<BolsaCentro[]>([]);
  const [metricas, setMetricas] = useState<MetricasCentro | null>(null);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState<EstadoBolsa | 'TODAS'>('TODAS');
  const [busqueda, setBusqueda] = useState('');
  // SPEC-113 (RN-003) — estado independiente del buscador de la pestaña Cotizaciones (SPEC-107).
  const [busquedaPipeline, setBusquedaPipeline] = useState('');
  const [drawerBolsaId, setDrawerBolsaId] = useState<string | null>(null);
  // SPEC-100 (RN-001, RN-005) — vive por encima de las pestañas: no se resetea al cambiar de tab.
  // `rangoControl` refleja lo que el usuario ve/edita (puede ser inválido momentáneamente);
  // `rangoAplicado` es el último rango válido y es el único que dispara `cargarTodo` (RN-003).
  const rangoPorDefecto = useMemo(() => calcularRangoPorDefecto(), []);
  const [rangoControl, setRangoControl] = useState(rangoPorDefecto);
  const [rangoAplicado, setRangoAplicado] = useState(rangoPorDefecto);

  // SPEC-102 (RN-001) — el query param `estado` inicializa/sincroniza filtroEstado
  // sin tocar rangoAplicado, para no disparar cargarTodo (design.md §11).
  useEffect(() => {
    const estadoParam = searchParams.get('estado');
    if (estadoParam && (Object.values(EstadoBolsa) as string[]).includes(estadoParam)) {
      setFiltroEstado(estadoParam as EstadoBolsa);
      setTab('cotizaciones');
    }
  }, [searchParams]);

  const cargarTodo = useCallback(async (rango: { desde: Date; hasta: Date }) => {
    setLoading(true);
    try {
      const filtroFechas = { desde: formatearFechaApi(rango.desde), hasta: formatearFechaApi(rango.hasta) };
      const [abiertas, listado, metricasData] = await Promise.all([
        centroApi.listarBolsasAbiertas(filtroFechas),
        centroApi.listarCentro({ limit: 500, ...filtroFechas }),
        centroApi.obtenerMetricas(filtroFechas),
      ]);
      setBolsasAbiertas(abiertas);
      setCentro(listado.items);
      setMetricas(metricasData);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    cargarTodo(rangoAplicado);
  }, [cargarTodo, rangoAplicado]);

  // SPEC-128 (RN-002, VAL-001): mismo patrón ya certificado en
  // DetalleCotizacion/index.tsx (SPEC-124) — lee el flag de auto-apertura una sola vez y
  // limpia location.state con replace para que no se reabra en refresh/back.
  useEffect(() => {
    const state = location.state as { autoOpenNuevaBolsaModal?: boolean } | null;
    if (state?.autoOpenNuevaBolsaModal) {
      setNuevaBolsaModalOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // SPEC-128 (TASK-003, RN-002): mismo patrón certificado de creación + navegación que
  // CrearBolsaPage.handleCrearBolsa / DetalleCotizacion.handleCrearBolsa (SPEC-115) — si
  // BolsasService.crearBolsa rechaza, NuevaBolsaModal captura el error y mantiene el modal
  // abierto con los datos ingresados (CA-004); no se navega.
  const handleCrearBolsa = useCallback(async (dto: CreateBolsaDTO) => {
    const nuevaBolsa = await BolsasService.crearBolsa(dto);
    navigate(`/cotizaciones/detalle/${nuevaBolsa.id}`, { state: { autoOpenAgregarProducto: true } });
  }, [navigate]);

  // SPEC-138 (RN-001, RN-004, RN-005): el botón "Nueva bolsa" del Centro ya no navega a
  // /catalogo?nuevaBolsa=1. Reutiliza resolverDestinoCrear() —el mismo servicio del ítem
  // "Crear" del menú lateral (SPEC-124/SPEC-128)— sin lógica de selección de bolsa inline
  // (VAL-001). Si el destino es una ruta distinta a la actual, navega con el flag de
  // auto-apertura; si es la misma ruta (/cotizaciones, fallback sin bolsas abiertas o ante
  // error), abre el modal por estado local, porque navegar a sí misma no re-dispararía el
  // useEffect de montaje que consume el flag (VAL-002). resolverDestinoCrear() ya absorbe el
  // error con su try/catch interno, así que aquí no se agrega manejo propio (VAL-003).
  const handleNuevaBolsaClick = useCallback(async () => {
    setResolviendoDestino(true);
    try {
      const destino = await resolverDestinoCrear();
      if (destino.path !== location.pathname) {
        navigate(destino.path, { state: destino.state });
      } else {
        setNuevaBolsaModalOpen(true);
      }
    } finally {
      setResolviendoDestino(false);
    }
  }, [navigate, location.pathname]);

  const handleCambiarRango = useCallback((nuevoRango: { desde: Date; hasta: Date }) => {
    setRangoControl(nuevoRango);
    // SPEC-100 (RN-003) — nunca disparar peticiones con desde > hasta; se conserva el
    // último rango aplicado válido hasta que el usuario corrija la entrada.
    if (nuevoRango.desde <= nuevoRango.hasta) {
      setRangoAplicado(nuevoRango);
    }
  }, []);

  const refrescar = useCallback(() => {
    cargarTodo(rangoAplicado);
  }, [cargarTodo, rangoAplicado]);

  const cotizaciones = useMemo(
    () => centro.filter((b) => b.estado_codigo !== EstadoBolsa.ACTIVA),
    [centro],
  );

  const filtradas = useMemo(() => {
    let lista = cotizaciones;
    if (filtroEstado !== 'TODAS') {
      lista = lista.filter((b) => b.estado_codigo === filtroEstado);
    }
    if (busqueda.trim()) {
      const q = busqueda.trim().toLowerCase();
      lista = lista.filter(
        (b) =>
          (b.codigo_cotizacion ?? b.id).toLowerCase().includes(q) ||
          (b.cliente_razon_social ?? '').toLowerCase().includes(q),
      );
    }
    return lista;
  }, [cotizaciones, filtroEstado, busqueda]);

  // SPEC-113 (RN-002) — filtro de Pipeline por cliente, independiente de `filtradas` (RN-003).
  const cotizacionesPipeline = useMemo(
    () => filtrarPorCliente(cotizaciones, busquedaPipeline),
    [cotizaciones, busquedaPipeline],
  );

  const handleFiltrarPorEstado = useCallback((estado: EstadoBolsa | 'TODAS') => {
    setFiltroEstado(estado);
    setTab('cotizaciones');
  }, []);

  const handleAbrirDrawer = useCallback((id: string) => {
    setDrawerBolsaId(id);
  }, []);

  // CA-008 (SPEC-103) — al navegar a la edición completa desde el drawer, conserva
  // pestaña/filtro/drawer para restaurarlos al volver. Se usa sessionStorage en vez de
  // location.state: el botón "atrás" nativo del navegador restaura la entrada de historial
  // de /cotizaciones tal como quedó ANTES de navegar hacia adelante (sin el state que se le
  // adjuntó a la entrada de destino), así que location.state nunca llega poblado al volver.
  useEffect(() => {
    const raw = sessionStorage.getItem(VOLVER_A_KEY);
    if (!raw) return;
    sessionStorage.removeItem(VOLVER_A_KEY);
    try {
      const volverA = JSON.parse(raw) as { tab: TabId; filtroEstado: EstadoBolsa | 'TODAS'; drawerBolsaId: string };
      setTab(volverA.tab);
      setFiltroEstado(volverA.filtroEstado);
      setDrawerBolsaId(volverA.drawerBolsaId);
    } catch {
      // JSON corrupto o inesperado — se ignora, la página abre en su estado por defecto
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // SPEC-114 (RN-002) — generaliza handleVerEdicion: navega directo al detalle desde
  // cualquiera de las 3 vistas y para cualquier estado de bolsa, preservando la misma
  // lógica de retorno vía sessionStorage (VOLVER_A_KEY) que ya usaba el drawer.
  const handleVerDetalle = useCallback(
    (bolsaId: string) => {
      sessionStorage.setItem(VOLVER_A_KEY, JSON.stringify({ tab, filtroEstado, drawerBolsaId: bolsaId }));
      navigate(`/cotizaciones/detalle/${bolsaId}`);
    },
    [navigate, tab, filtroEstado],
  );

  // SPEC-206 (UC-009/CA-029) — tras duplicar se abre el Detalle de la copia con el toast y el
  // resumen de re-precio; al volver, el Centro reabre el drawer de la copia.
  const handleDuplicada = useCallback(
    (resultado: BolsaConReprecio, codigoOrigen: string) => {
      const copia = resultado.bolsa;
      sessionStorage.setItem(VOLVER_A_KEY, JSON.stringify({ tab, filtroEstado, drawerBolsaId: copia.id }));
      navigate(`/cotizaciones/detalle/${copia.id}`, {
        state: {
          duplicado: {
            titulo: `${copia.codigo_cotizacion ?? copia.id} creada a partir de ${codigoOrigen}`,
            reprecio: resultado.reprecio,
          },
        },
      });
    },
    [navigate, tab, filtroEstado],
  );

  return (
    <Box sx={{ backgroundColor: TOKENS.bg, minHeight: '100%' }}>
      <Box sx={{ width: '97%', margin: '0 auto', padding: '44px 48px 90px' }}>
        {/* Cabecera de página */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '28px' }}>
          <Box>
            <Typography
              sx={{
                fontFamily: 'Poppins, sans-serif',
                fontSize: fs(11),
                fontWeight: 600,
                letterSpacing: '2.2px',
                textTransform: 'uppercase',
                color: TOKENS.muted,
                marginBottom: '6px',
              }}
            >
              CATÁLOGO · COTIZACIONES
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Poppins, sans-serif',
                fontSize: fs(37),
                fontWeight: 700,
                letterSpacing: '-0.8px',
                color: '#000000',
              }}
            >
              Centro de <Box component="span" sx={{ color: '#000000' }}>Cotizaciones</Box>
            </Typography>
            {/* SPEC-113 (RN-001) — descripción estática, común a las 3 pestañas */}
            <Typography
              data-testid="centro-cotizaciones-descripcion"
              sx={{
                fontFamily: 'Poppins, sans-serif',
                fontSize: fs(14),
                color: TOKENS.muted,
                marginTop: '4px',
              }}
            >
              Gestiona el ciclo completo de tus cotizaciones: desde la creación de la bolsa hasta el envío al cliente.
            </Typography>
          </Box>
          <Button
            data-testid="btn-nueva-bolsa-centro"
            startIcon={<AddIcon />}
            // SPEC-138 (RN-001, RN-005): resuelve el destino con resolverDestinoCrear() en vez
            // de navegar a /catalogo?nuevaBolsa=1. Deshabilitado mientras resuelve (RN-006).
            onClick={handleNuevaBolsaClick}
            disabled={resolviendoDestino}
            sx={{
              borderRadius: '999px',
              border: `1px solid ${TOKENS.purple}`,
              color: TOKENS.purple,
              textTransform: 'none',
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 600,
              padding: '8px 18px',
              '&:hover': { backgroundColor: TOKENS.purple, color: '#fff' },
              '&.Mui-disabled': { opacity: 0.55, color: TOKENS.purple, borderColor: TOKENS.purple },
            }}
          >
            Nueva bolsa
          </Button>
        </Box>

        {/* Control de rango de fechas (RN-001, RN-002, RN-005) */}
        <Box sx={{ marginBottom: '20px' }}>
          <DateRangeControl desde={rangoControl.desde} hasta={rangoControl.hasta} onChange={handleCambiarRango} />
        </Box>

        {/* Pestañas — SPEC-105 (CH-02): segmented control tipo píldora */}
        <Box
          sx={{
            display: 'inline-flex',
            gap: '4px',
            backgroundColor: TOKENS.wash,
            border: `1px solid ${TOKENS.hair}`,
            borderRadius: '999px',
            padding: '6px',
            marginBottom: '34px',
          }}
        >
          {TABS.map((t) => {
            const activo = tab === t.id;
            return (
              <Box
                key={t.id}
                data-testid={`tab-${t.id}`}
                onClick={() => setTab(t.id)}
                sx={{
                  cursor: 'pointer',
                  borderRadius: '999px',
                  padding: '11px 26px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all .18s ease',
                  backgroundColor: activo ? '#fff' : 'transparent',
                  boxShadow: activo ? '0 3px 6px rgba(46,36,48,.07), 0 14px 30px rgba(46,36,48,.15)' : 'none',
                  '&:hover': activo ? {} : { color: TOKENS.ink },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: fs(14),
                    fontWeight: activo ? 700 : 500,
                    color: activo ? TOKENS.purple : TOKENS.muted,
                  }}
                >
                  {t.label}
                </Typography>
                {t.id === 'pipeline' && (
                  <Typography
                    component="span"
                    sx={{
                      fontFamily: 'Poppins, sans-serif',
                      fontSize: fs(11),
                      fontWeight: 700,
                      color: activo ? '#fff' : TOKENS.faint,
                      backgroundColor: activo ? TOKENS.purple : 'transparent',
                      borderRadius: '999px',
                      padding: activo ? '1px 7px' : 0,
                    }}
                  >
                    ({bolsasAbiertas.length})
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>

        {/* Contenido con fade */}
        <Box
          key={tab}
          sx={{
            animation: 'fadeInCentro .25s ease',
            '@keyframes fadeInCentro': {
              from: { opacity: 0, transform: 'translateY(4px)' },
              to: { opacity: 1, transform: 'translateY(0)' },
            },
          }}
        >
          {tab === 'cotizaciones' && (
            <CotizacionesTab
              loading={loading}
              metricas={metricas}
              bolsasAbiertas={bolsasAbiertas}
              cotizaciones={cotizaciones}
              filtradas={filtradas}
              filtroEstado={filtroEstado}
              busqueda={busqueda}
              onFiltrar={handleFiltrarPorEstado}
              onBuscar={setBusqueda}
              onAbrirDrawer={handleAbrirDrawer}
              onVerBolsasAbiertas={() => setTab('pipeline')}
              onVerDetalle={handleVerDetalle}
            />
          )}
          {tab === 'pipeline' && (
            <PipelineTab
              bolsasAbiertas={bolsasAbiertas}
              cotizacionesPipeline={cotizacionesPipeline}
              busquedaPipeline={busquedaPipeline}
              onBuscarPipeline={setBusquedaPipeline}
              onRefrescar={refrescar}
              onAbrirDrawer={handleAbrirDrawer}
              onVerDetalle={handleVerDetalle}
            />
          )}
          {tab === 'resumen' && (
            <ResumenTab
              metricas={metricas}
              bolsasAbiertas={bolsasAbiertas}
              cotizaciones={cotizaciones}
              onAbrirDrawer={handleAbrirDrawer}
              onVerBolsasAbiertas={() => setTab('pipeline')}
              onFiltrar={handleFiltrarPorEstado}
              onVerDetalle={handleVerDetalle}
            />
          )}
        </Box>
      </Box>

      {drawerBolsaId && (
        <QuoteDrawer
          bolsaId={drawerBolsaId}
          onClose={() => setDrawerBolsaId(null)}
          onCambio={refrescar}
          onVerEdicion={handleVerDetalle}
          onAbrirDrawer={handleAbrirDrawer}
          onDuplicada={handleDuplicada}
        />
      )}

      {/* SPEC-128 (RN-002, RN-003): cancelar no navega fuera de /cotizaciones — solo cierra. */}
      <NuevaBolsaModal
        open={nuevaBolsaModalOpen}
        onClose={() => setNuevaBolsaModalOpen(false)}
        onCreateBolsa={handleCrearBolsa}
      />
    </Box>
  );
};

export const CentroCotizacionesPage: React.FC = () => (
  <ToastProvider>
    <CentroCotizacionesContent />
  </ToastProvider>
);

export default CentroCotizacionesPage;
